import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import type { EventContentArg, EventInput as FcEventInput, DatesSetArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { EventOccurrence, EventType, Person } from "@shared/types";
import { occurrenceColor, participantBadges } from "@/utils/eventColor";
import { ParticipantBadges } from "./ParticipantBadges";
import type { ParticipantBadge } from "./ParticipantBadges";
import { AgendaView } from "./AgendaView";
import {
  APP_TIMEZONE,
  addMonthsToMonthStart,
  formatMonthTitle,
  formatTime,
  startOfMonthIso,
} from "@/utils/date";
import { toNaiveIsoString, zonedWallClockToUtc } from "@shared/timezone";

export type CalendarViewName = "dayGridMonth" | "timeGridWeek" | "timeGridDay" | "listMonth";

export interface CalendarViewHandle {
  today: () => void;
  prev: () => void;
  next: () => void;
  changeView: (view: CalendarViewName) => void;
}

interface CalendarViewProps {
  events: EventOccurrence[];
  people: Person[];
  eventTypes: EventType[];
  initialView: CalendarViewName;
  onRangeChange: (range: { start: string; end: string; title: string }) => void;
  onSelectOccurrence: (occurrence: EventOccurrence) => void;
  onCreateAt: (dateIso: string, allDay: boolean) => void;
  canCreate: boolean;
}

// FullCalendar core has no built-in support for named IANA timezones (that requires the
// paid moment-timezone plugin) — left alone, it silently treats a "Z"-suffixed UTC ISO
// string's raw digits as the display slot, which is wrong for America/Chicago. Instead we
// feed it "naive" local-looking strings (no offset) pre-converted to Chicago wall-clock
// time, and let it render those literally. Its callbacks then hand back plain Date objects
// built from those same naive numbers via the browser's local Date constructor — so we read
// them back with local getters (never .toISOString(), which would reinterpret them in the
// *browser's* zone) and re-anchor to a true UTC instant via zonedWallClockToUtc.
function chicagoInstantFromCalendarDate(date: Date): string {
  return zonedWallClockToUtc(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    APP_TIMEZONE,
  ).toISOString();
}

function EventPill({
  arg,
  color,
  badges,
  occurrence,
}: {
  arg: EventContentArg;
  color: string;
  badges: ParticipantBadge[];
  occurrence: EventOccurrence;
}) {
  const isMonth = arg.view.type === "dayGridMonth";
  return (
    <div
      className="flex w-full items-center gap-1.5 overflow-hidden rounded-md px-1.5 py-0.5 text-left"
      style={
        isMonth
          ? { backgroundColor: `${color}1a`, borderLeft: `3px solid ${color}` }
          : { backgroundColor: color }
      }
    >
      <ParticipantBadges people={badges} size={isMonth ? 14 : 16} />
      {!arg.event.allDay && (
        <span
          className={isMonth ? "text-[11px] font-medium text-ink dark:text-ink-dark" : "text-[11px] font-medium text-white/90"}
        >
          {formatTime(occurrence.occurrenceStart)}
        </span>
      )}
      <span
        className={
          isMonth
            ? "truncate text-[11px] font-medium text-ink dark:text-ink-dark"
            : "truncate text-[12px] font-semibold text-white"
        }
      >
        {arg.event.title}
      </span>
    </div>
  );
}

export const CalendarView = forwardRef<CalendarViewHandle, CalendarViewProps>(function CalendarView(
  { events, people, eventTypes, initialView, onRangeChange, onSelectOccurrence, onCreateAt, canCreate },
  ref,
) {
  const calendarRef = useRef<FullCalendar | null>(null);
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const eventTypesById = useMemo(() => new Map(eventTypes.map((t) => [t.id, t])), [eventTypes]);

  const [activeView, setActiveView] = useState<CalendarViewName>(initialView);
  const [monthAnchor, setMonthAnchor] = useState<string>(() => startOfMonthIso(new Date()));
  const isAgenda = activeView === "listMonth";

  useImperativeHandle(
    ref,
    () => ({
      today: () => {
        if (isAgenda) setMonthAnchor(startOfMonthIso(new Date()));
        else calendarRef.current?.getApi().today();
      },
      prev: () => {
        if (isAgenda) setMonthAnchor((anchor) => addMonthsToMonthStart(anchor, -1));
        else calendarRef.current?.getApi().prev();
      },
      next: () => {
        if (isAgenda) setMonthAnchor((anchor) => addMonthsToMonthStart(anchor, 1));
        else calendarRef.current?.getApi().next();
      },
      changeView: (view: CalendarViewName) => {
        const wasAgenda = isAgenda;
        if (view === "listMonth") {
          // Keep showing whatever month the grid was on, for continuity.
          const currentDate = calendarRef.current?.getApi().getDate();
          if (currentDate) setMonthAnchor(startOfMonthIso(currentDate));
        } else if (!wasAgenda) {
          // Switching between grid views — FullCalendar stays mounted, so drive it directly.
          calendarRef.current?.getApi().changeView(view);
        }
        // Switching from agenda to a grid view remounts FullCalendar with the right
        // initialView below, so no imperative call is needed in that case.
        setActiveView(view);
      },
    }),
    [isAgenda],
  );

  // Agenda mode has no FullCalendar instance to report a range via datesSet, so it reports
  // its own — a full calendar month starting at monthAnchor, matching the grid views' scope.
  useEffect(() => {
    if (!isAgenda) return;
    onRangeChange({
      start: monthAnchor,
      end: addMonthsToMonthStart(monthAnchor, 1),
      title: formatMonthTitle(monthAnchor),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgenda, monthAnchor]);

  const fcEvents: FcEventInput[] = useMemo(
    () =>
      events.map((occ) => ({
        id: occ.occurrenceId,
        title: occ.title,
        start: occ.allDay ? occ.occurrenceStart.slice(0, 10) : toNaiveIsoString(new Date(occ.occurrenceStart), APP_TIMEZONE),
        end: occ.allDay ? occ.endAt.slice(0, 10) : toNaiveIsoString(new Date(occ.endAt), APP_TIMEZONE),
        allDay: occ.allDay,
        backgroundColor: occurrenceColor(occ, eventTypesById),
        borderColor: "transparent",
        extendedProps: { occurrence: occ, badges: participantBadges(occ, peopleById) },
      })),
    [events, peopleById, eventTypesById],
  );

  const handleDatesSet = (arg: DatesSetArg) => {
    onRangeChange({
      start: chicagoInstantFromCalendarDate(arg.start),
      end: chicagoInstantFromCalendarDate(arg.end),
      title: arg.view.title,
    });
  };

  if (isAgenda) {
    return <AgendaView events={events} people={people} eventTypes={eventTypes} onSelectOccurrence={onSelectOccurrence} />;
  }

  return (
    <div className="fc-shell min-h-0 flex-1">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={activeView}
        headerToolbar={false}
        height="100%"
        now={() => toNaiveIsoString(new Date(), APP_TIMEZONE)}
        firstDay={0}
        nowIndicator
        dayMaxEvents={3}
        selectable={canCreate}
        selectMirror
        navLinks={false}
        eventDisplay="block"
        datesSet={handleDatesSet}
        events={fcEvents}
        eventContent={(arg) => (
          <EventPill
            arg={arg}
            color={(arg.event.backgroundColor as string) || "#6B7280"}
            badges={arg.event.extendedProps.badges as ParticipantBadge[]}
            occurrence={arg.event.extendedProps.occurrence as EventOccurrence}
          />
        )}
        eventClick={(arg) => {
          const occurrence = arg.event.extendedProps.occurrence as EventOccurrence;
          onSelectOccurrence(occurrence);
        }}
        dateClick={(arg) => {
          if (canCreate) onCreateAt(chicagoInstantFromCalendarDate(arg.date), arg.allDay);
        }}
        select={(arg) => {
          if (canCreate) onCreateAt(chicagoInstantFromCalendarDate(arg.start), arg.allDay);
        }}
      />
    </div>
  );
});
